import { useEffect, useRef, useState } from "react";
import {
    createVideoTemplateDev,
    updateVideoTemplateDev,
    planVideoTemplatePreview,
    startVideoTemplatePreview,
    getVideoTemplatePreviewStatus,
    fetchFlyerCategoryOptions,
    type FlyerCategoryOption,
    type VideoTemplateInit,
    type TemplateFieldDef,
} from "./videoTemplateService";
import TemplateFieldsEditor from "../common/TemplateFieldsEditor";
import styles from "../common/FlyerTemplateDevForm.module.css";

interface Props {
    onCreated: () => void;
    onError: (msg: string) => void;
    initialTemplate?: VideoTemplateInit | null;
    onCancelEdit?: () => void;
}

const CAMERA_MOVEMENT_OPTIONS = [
    "Drone fly-through",
    "Smooth cinematic pan",
    "Slow zoom",
    "Orbit shot",
    "Parallax movement",
    "FPV style",
    "Tracking shot",
    "Static with parallax",
    "Handheld documentary",
    "Hyperlapse",
];

const MOTION_STYLE_OPTIONS = [
    "Slow motion (luxury)",
    "Dynamic energetic",
    "Smooth luxury pacing",
    "Fast-cut reel",
    "Emotional cinematic",
    "Travel vlog style",
    "Editorial fashion-forward",
];

const TRANSITION_STYLE_OPTIONS = [
    "Smooth fades",
    "Speed ramp",
    "Light leaks",
    "Luxury blur transitions",
    "Cinematic wipes",
    "Film transitions",
    "Whip pan",
    "Match cut",
    "Dissolve",
];

const TEXT_ANIMATION_OPTIONS = [
    "Elegant fade-up",
    "Luxury typography reveal",
    "Modern kinetic typography",
    "Minimal floating text",
    "Travel magazine style",
    "Bold impact reveal",
    "Soft slide-in",
];

const AUDIO_MOOD_OPTIONS = [
    "Luxury chill",
    "Tropical deep house",
    "Inspirational cinematic",
    "Corporate premium",
    "Vacation vibes",
    "Emotional piano",
    "Epic orchestral",
    "Lo-fi relaxed",
    "Upbeat tropical pop",
];

const ASPECT_RATIO_OPTIONS = ["9:16", "16:9", "1:1", "4:5"];

// Reel styling forwarded to the videoagent platform (USE_VIDEO_V2 only).
// Must match remotion_renderer/src/schema.ts's enums exactly — anything else
// falls back to the platform's own default rather than failing generation.
const FONT_FAMILY_OPTIONS = ["Inter", "Montserrat", "Playfair Display", "Poppins", "Cormorant Garamond"];
const OVERLAY_STYLE_OPTIONS = ["minimal", "bold", "elegant"];
const OVERLAY_ANIMATION_OPTIONS = ["fade", "slideUp", "pop", "none", "letterReveal", "wordPunch"];
const OVERLAY_DECORATION_OPTIONS = ["underline", "gradientShimmer", "none"];

function useInitState<T>(init: () => T, dep: unknown): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [v, setV] = useState<T>(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setV(init()); }, [dep]);
    return [v, setV];
}

async function fetchUrlAsFile(url: string, filename: string): Promise<File> {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Could not download the generated preview video.");
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || "video/mp4" });
}

function validateFields(fields: TemplateFieldDef[]): string | null {
    if (fields.length === 0) return "Add at least one form field.";
    const keys = new Set<string>();
    for (const f of fields) {
        if (!f.label.trim()) return "Each field needs a label.";
        if (!f.key.trim()) return "Each field needs a key.";
        if (keys.has(f.key)) return `Duplicate field key: ${f.key}`;
        keys.add(f.key);
    }
    return null;
}

export default function VideoTemplateDevForm({ onCreated, onError, initialTemplate, onCancelEdit }: Props) {
    const isEdit = Boolean(initialTemplate);
    const [categories, setCategories] = useState<FlyerCategoryOption[]>([]);
    const [open, setOpen] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [templateVideoFile, setTemplateVideoFile] = useState<File | null>(null);
    const [generatingPreview, setGeneratingPreview] = useState(false);
    const unmountedRef = useRef(false);
    useEffect(() => {
        // Reset on (re)mount, not just cleanup — StrictMode's dev-only
        // mount→unmount→remount cycle otherwise leaves this stuck `true`
        // forever after the first mount, since the cleanup below runs once
        // during that simulated unmount and nothing ever flips it back.
        unmountedRef.current = false;
        return () => { unmountedRef.current = true; };
    }, []);

    // Core fields — reinitialise when template changes (edit mode)
    const [categorySlug, setCategorySlug] = useInitState(() => initialTemplate?.category ?? "", initialTemplate?.id);
    const [name, setName] = useInitState(() => initialTemplate?.name ?? "", initialTemplate?.id);
    const [basePrompt, setBasePrompt] = useInitState(
        () => initialTemplate?.base_prompt ?? initialTemplate?.prompt_template ?? "",
        initialTemplate?.id,
    );
    const [fields, setFields] = useInitState(() => initialTemplate?.fields ?? [], initialTemplate?.id);
    const [ctaText] = useInitState(() => initialTemplate?.cta_text ?? "BOOK NOW", initialTemplate?.id);
    const [tagline, setTagline] = useInitState(() => initialTemplate?.tagline ?? "", initialTemplate?.id);
    const [mood, setMood] = useInitState(() => initialTemplate?.mood ?? "", initialTemplate?.id);
    const [aspectRatio, setAspectRatio] = useInitState(() => initialTemplate?.aspect_ratio ?? "9:16", initialTemplate?.id);

    // Video production settings
    const [cameraMovement, setCameraMovement] = useInitState(() => initialTemplate?.camera_movement ?? "", initialTemplate?.id);
    const [motionStyle, setMotionStyle] = useInitState(() => initialTemplate?.motion_style ?? "", initialTemplate?.id);
    const [transitionStyle, setTransitionStyle] = useInitState(() => initialTemplate?.transition_style ?? "", initialTemplate?.id);
    const [textAnimationStyle, setTextAnimationStyle] = useInitState(() => initialTemplate?.text_animation_style ?? "", initialTemplate?.id);
    const [audioMood, setAudioMood] = useInitState(() => initialTemplate?.audio_mood ?? "", initialTemplate?.id);
    const [sceneStructure, setSceneStructure] = useInitState(() => initialTemplate?.scene_structure ?? "", initialTemplate?.id);
    const [videoDuration, setVideoDuration] = useInitState<5 | 10>(() => (initialTemplate?.video_duration as 5 | 10) ?? 10, initialTemplate?.id);

    // Reel styling — forwarded to the videoagent platform (USE_VIDEO_V2 only)
    const [brandColor, setBrandColor] = useInitState(() => initialTemplate?.brand_color ?? "", initialTemplate?.id);
    const [fontFamily, setFontFamily] = useInitState(() => initialTemplate?.font_family ?? "", initialTemplate?.id);
    const [overlayStyle, setOverlayStyle] = useInitState(() => initialTemplate?.overlay_style ?? "", initialTemplate?.id);
    const [overlayAnimation, setOverlayAnimation] = useInitState(() => initialTemplate?.overlay_animation ?? "", initialTemplate?.id);
    const [overlayDecoration, setOverlayDecoration] = useInitState(() => initialTemplate?.overlay_decoration ?? "", initialTemplate?.id);

    useEffect(() => {
        if (isEdit) setOpen(true);
    }, [isEdit, initialTemplate?.id]);

    useEffect(() => {
        let cancelled = false;
        void fetchFlyerCategoryOptions()
            .then((data: FlyerCategoryOption[]) => { if (!cancelled) setCategories(data); })
            .catch(() => { if (!cancelled) onError("Could not load category list"); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buildPromptFromSettings = () => {
        const parts: string[] = [];

        const dest = fields.find(f => f.key === "destination");
        const destPlaceholder = dest ? "{destination}" : "your destination";

        parts.push(`A cinematic travel video showcasing ${destPlaceholder}.`);

        if (sceneStructure.trim()) {
            parts.push(`\nSCENE STRUCTURE:\n${sceneStructure.trim()}`);
        }

        const production: string[] = [];
        if (cameraMovement) production.push(`${cameraMovement} camera movement`);
        if (motionStyle) production.push(motionStyle);
        if (transitionStyle) production.push(`${transitionStyle} transitions`);
        if (textAnimationStyle) production.push(`${textAnimationStyle} text animation`);
        if (audioMood) production.push(`${audioMood} background music`);
        if (mood.trim()) production.push(`${mood.trim()} mood`);
        if (production.length > 0) {
            parts.push(`\nPRODUCTION STYLE: ${production.join(", ")}.`);
        }

        const textParts: string[] = [];
        fields.filter(f => !["image_url","video_url","first_frame_image_url"].includes(f.key))
              .forEach(f => textParts.push(`${f.label}: {${f.key}}`));
        if (tagline.trim()) textParts.push(`Tagline: "${tagline.trim()}"`);
        if (ctaText.trim()) textParts.push(`CTA: "${ctaText.trim()}"`);
        if (textParts.length > 0) {
            parts.push(`\nTEXT OVERLAYS: ${textParts.join(" | ")}`);
        }

        setBasePrompt(parts.join(""));
    };

    const handleGeneratePreview = async () => {
        if (!basePrompt.trim()) {
            onError("Add a base prompt first — Generate Preview uses it to create the video.");
            return;
        }
        setGeneratingPreview(true);
        try {
            const plan = await planVideoTemplatePreview(basePrompt.trim(), {
                aspect_ratio: aspectRatio,
                duration_hint_seconds: videoDuration,
            });
            const started = await startVideoTemplatePreview(plan.timeline);
            // Real generation can take several minutes — poll instead of one
            // long request, which a reverse proxy can kill before the answer
            // comes back even though the video finished fine.
            while (!unmountedRef.current) {
                await new Promise((resolve) => setTimeout(resolve, 4000));
                const poll = await getVideoTemplatePreviewStatus(started.preview_id);
                if (unmountedRef.current) return;
                if (poll.status === "completed") {
                    const file = await fetchUrlAsFile(poll.video_url, `generated_preview_${poll.job_id}.mp4`);
                    setTemplateVideoFile(file);
                    return;
                }
                if (poll.status === "failed") {
                    onError(poll.error || "Failed to generate preview video.");
                    return;
                }
            }
        } catch (e) {
            if (!unmountedRef.current) onError(e instanceof Error ? e.message : "Failed to generate preview video.");
        } finally {
            if (!unmountedRef.current) setGeneratingPreview(false);
        }
    };

    const handleSubmit = async () => {
        if (!categorySlug || !name.trim()) {
            onError("Category and name are required.");
            return;
        }
        if (!basePrompt.trim() && !sceneStructure.trim()) {
            onError("Add a base prompt or scene structure — used as the visual foundation when users describe the reel in chat.");
            return;
        }
        if (!isEdit && !templateVideoFile) {
            onError("Video file is required.");
            return;
        }
        const fieldErr = validateFields(fields);
        if (fieldErr) { onError(fieldErr); return; }

        setSubmitting(true);
        try {
            const payload = {
                category_slug: categorySlug,
                name: name.trim(),
                fields,
                cta_text: ctaText,
                tagline: tagline.trim(),
                mood: mood.trim(),
                aspect_ratio: aspectRatio,
                base_prompt: basePrompt.trim(),
                prompt_template: "",
                camera_movement: cameraMovement,
                motion_style: motionStyle,
                transition_style: transitionStyle,
                text_animation_style: textAnimationStyle,
                audio_mood: audioMood,
                scene_structure: sceneStructure.trim(),
                video_duration: videoDuration,
                brand_color: brandColor.trim(),
                font_family: fontFamily,
                overlay_style: overlayStyle,
                overlay_animation: overlayAnimation,
                overlay_decoration: overlayDecoration,
            };

            if (isEdit && initialTemplate) {
                await updateVideoTemplateDev(initialTemplate.id, { ...payload, template_video: templateVideoFile ?? undefined });
            } else {
                await createVideoTemplateDev({ ...payload, template_video: templateVideoFile! });
                setName(""); setTagline(""); setBasePrompt(""); setSceneStructure("");
                setTemplateVideoFile(null); setFields([]);
            }
            onCreated();
        } catch (e) {
            onError(e instanceof Error ? e.message : "Failed to save template.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className={styles.panel}
            aria-label={isEdit ? `Edit video template: ${initialTemplate?.name ?? ""}` : "Create video template"}
            style={{
                borderColor: isEdit ? "rgba(251,191,36,0.4)" : "rgba(139,92,246,0.35)",
                background: isEdit
                    ? "linear-gradient(145deg, rgba(251,191,36,0.07) 0%, rgba(15,23,42,0.4) 100%)"
                    : "linear-gradient(145deg, rgba(139,92,246,0.1) 0%, rgba(15,23,42,0.4) 100%)",
            }}
        >
            <button
                type="button"
                className={styles.toggle}
                style={{ color: "#c4b5fd" }}
                onClick={() => setOpen(v => !v)}
            >
                <span>{isEdit ? `Editing video: ${initialTemplate?.name ?? "template"}` : "Create video template"}</span>
                <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                    {isEdit && onCancelEdit && (
                        <span
                            className={styles.badge}
                            style={{ background: "rgba(239,68,68,0.2)", color: "#fca5a5", cursor: "pointer" }}
                            onClick={e => { e.stopPropagation(); onCancelEdit(); }}
                        >
                            Cancel edit
                        </span>
                    )}
                    <span className={styles.badge} style={isEdit ? { background: "rgba(251,191,36,0.2)", color: "#fcd34d" } : { background: "rgba(139,92,246,0.2)", color: "#c4b5fd" }}>
                        {isEdit ? "Edit" : "Development"}
                    </span>
                    <span className={styles.badge} style={{ background: "rgba(139,92,246,0.2)", color: "#c4b5fd" }}>
                        🎬 Video
                    </span>
                </div>
            </button>

            {open && (
                <div className={styles.body}>
                    {!isEdit && (
                        <p className={styles.hint}>
                            Saves to MongoDB <code>templates</code> (asset_type=video). Upload a reference video — the thumbnail is auto-extracted from the first frame.
                            Only categories with at least one active template appear on the Videos tab.
                        </p>
                    )}

                    {/* ── Identity ── */}
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-category">Category</label>
                            <select
                                id="vt-category"
                                className={styles.select}
                                value={categorySlug}
                                onChange={e => setCategorySlug(e.target.value)}
                            >
                                <option value="">Select subcategory…</option>
                                {categories.map(c => (
                                    <option key={c.slug} value={c.slug}>
                                        {c.name} — {c.display_group}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="vt-name">Template name</label>
                            <input
                                id="vt-name"
                                className={styles.input}
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. Luxury resort cinematic"
                            />
                        </div>
                    </div>

                    {/* ── Scene Structure ── */}
                    <div className={styles.field}>
                        <label htmlFor="vt-scene">Scene structure &amp; timeline</label>
                        <p className={styles.promptHelp}>
                            <strong>Define how scenes unfold</strong> with timestamps — must cover the full <strong>{videoDuration}s</strong> clip (matches video duration above).
                            The backend validates scene end-times against video duration and returns an error if they don't match.
                        </p>
                        <textarea
                            id="vt-scene"
                            className={`${styles.textarea} ${styles.textareaMono}`}
                            rows={5}
                            value={sceneStructure}
                            onChange={e => setSceneStructure(e.target.value)}
                            placeholder={"Scene 1 (0–3s): Aerial establishing shot of destination landmark\nScene 2 (3–6s): Hotel / resort exterior and pool area\nScene 3 (6–8s): Guests enjoying activities / amenities\nScene 4 (8–9s): Sunset or atmospheric b-roll\nScene 5 (9–10s): Logo, tagline, and CTA overlay"}
                        />
                    </div>

                    {/* ── Video Production Settings ── */}
                    <div className={styles.fieldsEditor}>
                        <span className={styles.fieldsEditorTitle}>🎬 Video production settings</span>
                    </div>

                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-cam">Camera movement</label>
                            <select id="vt-cam" className={styles.select} value={cameraMovement} onChange={e => setCameraMovement(e.target.value)}>
                                <option value="">— Not specified —</option>
                                {CAMERA_MOVEMENT_OPTIONS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="vt-motion">Motion style</label>
                            <select id="vt-motion" className={styles.select} value={motionStyle} onChange={e => setMotionStyle(e.target.value)}>
                                <option value="">— Not specified —</option>
                                {MOTION_STYLE_OPTIONS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-trans">Transition style</label>
                            <select id="vt-trans" className={styles.select} value={transitionStyle} onChange={e => setTransitionStyle(e.target.value)}>
                                <option value="">— Not specified —</option>
                                {TRANSITION_STYLE_OPTIONS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="vt-text-anim">Text animation style</label>
                            <select id="vt-text-anim" className={styles.select} value={textAnimationStyle} onChange={e => setTextAnimationStyle(e.target.value)}>
                                <option value="">— Not specified —</option>
                                {TEXT_ANIMATION_OPTIONS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-audio">Audio / music mood</label>
                            <select id="vt-audio" className={styles.select} value={audioMood} onChange={e => setAudioMood(e.target.value)}>
                                <option value="">— Not specified —</option>
                                {AUDIO_MOOD_OPTIONS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="vt-mood">Overall mood / energy</label>
                            <input
                                id="vt-mood"
                                className={styles.input}
                                value={mood}
                                onChange={e => setMood(e.target.value)}
                                placeholder="e.g. luxury, romantic, adventurous"
                            />
                        </div>
                    </div>

                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-ratio">Aspect ratio</label>
                            <select id="vt-ratio" className={styles.select} value={aspectRatio} onChange={e => setAspectRatio(e.target.value)}>
                                {ASPECT_RATIO_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="vt-vdur">
                                Video duration
                                <span style={{ fontWeight: 400, color: "#64748b", fontSize: "0.75rem", marginLeft: 6 }}>
                                    (Kling: 5s or 10s)
                                </span>
                            </label>
                            <select
                                id="vt-vdur"
                                className={styles.select}
                                value={videoDuration}
                                onChange={e => setVideoDuration(Number(e.target.value) as 5 | 10)}
                            >
                                <option value={10}>10 seconds</option>
                                <option value={5}>5 seconds</option>
                            </select>
                        </div>
                    </div>
                    {/* CTA text / Tagline inputs intentionally not shown here — video
                        no longer bakes AI-written on-screen text (sellers add their own
                        text afterward via the Edit Text overlay editor), so these two
                        fields have nothing left to feed. `ctaText`/`tagline` state stays
                        at its default and is still submitted so the backend schema (shared
                        with flyer templates, which do still use these) is satisfied. */}

                    {/* ── Reel styling (videoagent platform, USE_VIDEO_V2 only) ── */}
                    <div className={styles.fieldsEditor}>
                        <span className={styles.fieldsEditorTitle}>🎨 Reel styling</span>
                    </div>
                    <p className={styles.promptHelp}>
                        Only applied when the video is rendered via the videoagent platform. Leave any of these
                        blank to use the platform&apos;s own default (#FFD24C, Inter, bold, fade, no decoration).
                    </p>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-brand-color">Brand color</label>
                            <input
                                id="vt-brand-color"
                                type="text"
                                className={styles.input}
                                value={brandColor}
                                onChange={e => setBrandColor(e.target.value)}
                                placeholder="#FFD24C"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="vt-font">Font family</label>
                            <select id="vt-font" className={styles.select} value={fontFamily} onChange={e => setFontFamily(e.target.value)}>
                                <option value="">— Platform default —</option>
                                {FONT_FAMILY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-overlay-style">Overlay style</label>
                            <select id="vt-overlay-style" className={styles.select} value={overlayStyle} onChange={e => setOverlayStyle(e.target.value)}>
                                <option value="">— Platform default —</option>
                                {OVERLAY_STYLE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="vt-overlay-anim">Text animation</label>
                            <select id="vt-overlay-anim" className={styles.select} value={overlayAnimation} onChange={e => setOverlayAnimation(e.target.value)}>
                                <option value="">— Platform default —</option>
                                {OVERLAY_ANIMATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="vt-overlay-decor">Text decoration</label>
                            <select id="vt-overlay-decor" className={styles.select} value={overlayDecoration} onChange={e => setOverlayDecoration(e.target.value)}>
                                <option value="">— Platform default —</option>
                                {OVERLAY_DECORATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* ── User Input Fields ── */}
                    <div className={styles.fieldsEditor}>
                        <span className={styles.fieldsEditorTitle}>User input fields</span>
                    </div>
                    <TemplateFieldsEditor fields={fields} onChange={setFields} />

                    {/* ── Base prompt (visual foundation for LLM-built video prompt) ── */}
                    <div className={styles.field}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                            <label htmlFor="vt-baseprompt" style={{ margin: 0 }}>
                                Base prompt
                                <span style={{ fontWeight: 400, color: "#64748b", fontSize: "0.75rem", marginLeft: 6 }}>(cinematic concept — merged with user chat at generation)</span>
                            </label>
                            <button
                                type="button"
                                onClick={buildPromptFromSettings}
                                style={{
                                    fontSize: 11, padding: "3px 10px", borderRadius: 6, cursor: "pointer",
                                    background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.35)",
                                    color: "#c4b5fd", whiteSpace: "nowrap", flexShrink: 0,
                                }}
                                title="Draft base prompt from production settings above"
                            >
                                ✦ Build from settings
                            </button>
                        </div>
                        <p className={styles.promptHelp}>
                            Visual and production direction for this reel template. Users describe the package in chat;
                            the server LLM combines their text with this base prompt, scene structure, and field keys — not a <code>{`{placeholder}`}</code> form.
                        </p>
                        <textarea
                            id="vt-baseprompt"
                            className={styles.textarea}
                            rows={8}
                            value={basePrompt}
                            onChange={e => setBasePrompt(e.target.value)}
                            placeholder="Cinematic luxury travel reel, golden-hour aerials, smooth pans, emotional pacing…"
                        />
                        <div style={{ marginTop: 8 }}>
                            <button
                                type="button"
                                onClick={() => void handleGeneratePreview()}
                                disabled={generatingPreview}
                                style={{
                                    fontSize: 12, padding: "6px 14px", borderRadius: 6,
                                    cursor: generatingPreview ? "wait" : "pointer",
                                    background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.35)",
                                    color: "#c4b5fd", opacity: generatingPreview ? 0.6 : 1,
                                }}
                                title="Generate a real sample video from this prompt via Video_generator_agent"
                            >
                                {generatingPreview ? "Generating video…" : "🎬 Generate Preview"}
                            </button>
                        </div>
                    </div>

                    {/* ── Template video upload ── */}
                    <div className={styles.field}>
                        <label htmlFor="vt-video">
                            Template video {isEdit && <span style={{ fontWeight: 400, color: "#64748b" }}>(leave blank to keep existing)</span>}
                        </label>
                        <p className={styles.promptHelp}>
                            Upload an mp4/mov/webm reference video, or use Generate Preview above. The thumbnail is auto-extracted from the first frame.
                        </p>
                        {templateVideoFile && (
                            <video
                                src={URL.createObjectURL(templateVideoFile)}
                                controls
                                style={{ height: 160, borderRadius: 6, marginBottom: 6, display: "block" }}
                            />
                        )}
                        {isEdit && initialTemplate?.preview_image_url && !templateVideoFile && (
                            <img
                                src={initialTemplate.preview_image_url}
                                alt="Current thumbnail (auto-extracted from video)"
                                style={{ height: 80, borderRadius: 6, marginBottom: 6, objectFit: "cover", display: "block" }}
                            />
                        )}
                        <input
                            id="vt-video"
                            type="file"
                            accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                            className={styles.fileInput}
                            onChange={e => setTemplateVideoFile(e.target.files?.[0] ?? null)}
                        />
                    </div>

                    {/* ── Submit ── */}
                    <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                        <button
                            type="button"
                            style={{
                                justifySelf: "start",
                                padding: "0.55rem 1.25rem",
                                borderRadius: 8,
                                border: "none",
                                background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                                color: "#f5f3ff",
                                fontSize: "0.8125rem",
                                fontWeight: 700,
                                cursor: submitting ? "wait" : "pointer",
                                opacity: submitting ? 0.55 : 1,
                            }}
                            disabled={submitting}
                            onClick={() => void handleSubmit()}
                        >
                            {submitting ? "Saving…" : isEdit ? "Update video template" : "Save video template"}
                        </button>
                        {isEdit && onCancelEdit && (
                            <button
                                type="button"
                                onClick={onCancelEdit}
                                style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.3)", borderRadius: 8, padding: "0.5rem 1rem", color: "#94a3b8", fontSize: "0.8125rem", cursor: "pointer" }}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
